import {
  ArrowLeft,
  ArrowRight,
  Star,
  Bike,
  Gift,
  UtensilsCrossed,
  SunSnow,
  Baby,
  Moon,
  CloudRain,
  TrainFront,
  Compass,
  Trees,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { Seo, PageSchema } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { GygTourBand } from "@/components/GetYourGuide";

const HERO_IMG = "/planner/115c33e22770.webp";

const SECTIONS = [
  {
    icon: Star,
    title: "The must-sees",
    body: "Nyhavn's coloured canal, Tivoli's lights, Rosenborg's crown jewels, the noon guard change at Amalienborg, the Marble Church and the Round Tower. The classics are classics for a reason — and they all fit inside one walkable city centre.",
    links: [
      { label: "The 3-day itinerary", to: "/copenhagen-itinerary" },
      { label: "Only 2 days? The 2-day plan", to: "/two-days-in-copenhagen" },
      { label: "In H.C. Andersen's footsteps", to: "/hc-andersen" },
      { label: "Indre By guide", to: "/areas/indre-by" },
      { label: "Full city map", to: "/map" },
    ],
  },
  {
    icon: Bike,
    title: "Do it like a local",
    body: "Swim in the harbour, bike everywhere, picnic in Kongens Have, queue for a cardamom bun and end the day on a bridge watching the sun go down. Copenhagen's best experiences are habits, not attractions.",
    links: [
      { label: "The 12 neighbourhoods", to: "/areas" },
      { label: "Copenhagen like a local", to: "/copenhagen-like-a-local" },
      { label: "Biking in Copenhagen", to: "/biking" },
      { label: "Getting around — the transport guide", to: "/transport" },
      { label: "Harbour swimming guide", to: "/harbour-swimming" },
      { label: "The bakery guide", to: "/bakeries" },
      { label: "If it rains", to: "/rainy-day" },
    ],
  },
  {
    icon: Gift,
    title: "Free things to do",
    body: "The guard change at Amalienborg, Kastellet's star fortress, the parks, the churches, Grundtvig's brick cathedral, Den Sorte Plads in Nørrebro's Superkilen park and the best view in town from a harbour bridge — all free.",
    links: [
      { label: "The full free list", to: "/budget#free-things" },
      { label: "Den Sorte Plads & Nørrebro", to: "/areas/norrebro" },
    ],
  },
  {
    icon: UtensilsCrossed,
    title: "Eat your way around",
    body: "Smørrebrød for lunch, street food at Reffen, a hotdog from a pølsevogn and pastries that justify the trip on their own. Come hungry.",
    links: [
      { label: "Where to eat smørrebrød", to: "/smorrebrod" },
      { label: "Traditional Danish food", to: "/danish-food" },
      { label: "Street food & food markets", to: "/copenhagen-food-markets" },
      { label: "The bakery crawl route", to: "/bakery-crawl" },
      { label: "Eat well for less", to: "/budget" },
    ],
  },
  {
    icon: SunSnow,
    title: "Seasonal Copenhagen",
    body: "Summer means harbour swims and 17-hour days; winter means candlelit hygge and Christmas markets. Pick your season — the city changes personality completely.",
    links: [
      { label: "Copenhagen by month — when to visit", to: "/copenhagen-in" },
      { label: "Visiting in winter (Tivoli closed)", to: "/copenhagen-in-winter" },
      { label: "Christmas in Copenhagen", to: "/christmas" },
      { label: "The Christmas walk route", to: "/christmas-walk" },
      { label: "The Christmas food guide", to: "/christmas-food" },
      { label: "All about gløgg", to: "/glogg" },
    ],
  },
  {
    icon: Baby,
    title: "With kids",
    body: "Tivoli, the Experimentarium science centre, the Blue Planet aquarium, the zoo, parks with playgrounds — and a city where cycling with kids is just normal life. Copenhagen is a family cheat code.",
    links: [
      { label: "The complete family guide", to: "/copenhagen-with-kids" },
      { label: "The 3-day family itinerary", to: "/copenhagen-with-kids-itinerary" },
      { label: "Where to stay with kids", to: "/stay" },
      { label: "Calm, family-friendly Østerbro", to: "/areas/osterbro" },
    ],
  },
  {
    icon: Moon,
    title: "At night",
    body: "Tivoli after dark, beer in Kødbyen, cocktails at one of Copenhagen's creative bars or a walk along Nyhavn. For nightlife go to Gothersgade, Sværtegade, Vestergade, Studiestræde, or Vesterbrogade.",
    links: [
      { label: "Indre By guide", to: "/areas/indre-by" },
      { label: "Vesterbro guide", to: "/areas/vesterbro" },
    ],
  },
  {
    icon: CloudRain,
    title: "Rainy days",
    body: "Pick one of Copenhagen's many impressive museums or have a look inside The Black Diamond library — it's free and impressive, Torvehallerne food market too. Browsing Magasin du Nord or Illum (high-end department store) costs nothing either. Rain is just hygge weather.",
    links: [
      { label: "What to pack, month by month", to: "/copenhagen-in" },
      { label: "The 3-day itinerary", to: "/copenhagen-itinerary" },
    ],
  },
  {
    icon: Trees,
    title: "New build & wild nature",
    body: "Ørestad is Copenhagen's bold experiment — 8TALLET, the VM Houses and the Mountain in one architecture stroll, with the giant Fields mall for cover if it rains. Right beside it, Kalvebod Fælled flips the script: wild meadows, wetland birds and big skies, ten minutes from the centre by metro.",
    links: [{ label: "The Amager guide", to: "/areas/amager" }],
  },
  {
    icon: TrainFront,
    title: "Day trips",
    body: "When the city is done: castles and the Louisiana art museum to the north, Viking ships in Roskilde, white cliffs at Møn — and Sweden in 40 minutes flat.",
    links: [
      { label: "Day trips from Copenhagen", to: "/trips" },
      { label: "The extreme day trip (EDT)", to: "/edt" },
      { label: "Short on time? The layover guide", to: "/layover" },
    ],
  },
  {
    icon: Compass,
    title: "Further away",
    body: "Willing to go further? Odense, Aarhus, Legoland in Billund, the white cliffs of Møn and Skagen's famous light — Denmark's gems beyond the capital, ordered by travel time.",
    links: [{ label: "Beyond Copenhagen", to: "/beyond-copenhagen" }],
  },
];

// Page-specific FAQ — mirrors the FAQPage JSON-LD below (answers must stay
// plain strings so the schema can reuse them; links render under the answer).
const FAQS = [
  {
    q: "What is the number one thing to do in Copenhagen?",
    a: "Tivoli Gardens is the classic — part amusement park, part garden, and magical after dark. Pair it with a walk through Nyhavn and the old town and you have the city's essence in a single day.",
    link: { to: "/copenhagen-itinerary", label: "The 3-day itinerary" },
  },
  {
    q: "What are the best free things to do in Copenhagen?",
    a: "The noon guard change at Amalienborg, the star-shaped Kastellet fortress, the Marble Church, the parks and the view from a harbour bridge — some of the city's best moments cost nothing.",
    link: { to: "/budget#free-things", label: "The full free list" },
  },
  {
    q: "What is there to do in Copenhagen when it rains?",
    a: "Museums like the Glyptoteket and the National Museum, the Black Diamond library, Torvehallerne food market or a long smørrebrød lunch — rain is just hygge weather here.",
    link: { to: "/rainy-day", label: "The rainy-day guide" },
  },
  {
    q: "What is there to do in Copenhagen at night?",
    a: "Tivoli after dark, beer bars in Kødbyen's old meatpacking district, cocktails in the inner city or an evening walk along Nyhavn. For late nightlife head to Gothersgade, Vestergade or Vesterbrogade.",
    link: { to: "/areas/vesterbro", label: "The Vesterbro guide" },
  },
  {
    q: "What should I do in Copenhagen in winter?",
    a: "Lean into the cosiness: Christmas markets and Tivoli's lights in December, candlelit cafés, museum days and a brave harbour sauna with a winter dip. The city is quieter and cheaper too.",
    links: [
      { to: "/christmas", label: "Christmas in Copenhagen" },
      { to: "/copenhagen-in", label: "Copenhagen by month" },
    ],
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function ThingsToDoPage() {
  return (
    <main data-testid="things-to-do-page" className="pt-16 md:pt-20">
      <Seo
        title="Things to Do in Copenhagen: Must-Sees, Local Spots & Free Activities"
        description="The best things to do in Copenhagen — must-see attractions, local spots, free activities, harbour swims and food, with links to every local guide."
        path="/things-to-do"
        image="https://cphhighlights.com/planner/115c33e22770.webp"
      />
      <PageSchema
        name="Things to Do in Copenhagen: Must-Sees, Local Spots & Free Activities"
        description="The best things to do in Copenhagen — must-see attractions, local spots, free activities, harbour swims and food, with links to every local guide."
        path="/things-to-do"
        crumb="Things to do"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative bg-[#13141A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Nyhavn's coloured houses along the canal, Copenhagen"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-28">
          <div className="flex items-center gap-3 mb-8">
            <Link
              to="/"
              data-testid="things-back-home"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black/40 text-white px-5 py-2.5 rounded-full border border-white/20 hover:bg-white hover:text-black transition-colors backdrop-blur-sm"
            >
              <ArrowLeft size={16} /> Back to start
            </Link>
            <ShareButton testid="things-share" />
          </div>
          <p className="font-body font-800 uppercase tracking-[0.2em] text-white text-xs">
            The complete menu
          </p>
          <h1 className="font-display font-900 tracking-tighter leading-[0.92] text-4xl sm:text-5xl lg:text-7xl mt-4 max-w-4xl">
            Things to do <span className="font-editorial italic font-600">in Copenhagen</span>
          </h1>
          <p className="font-editorial italic font-600 text-xl md:text-2xl text-white/85 mt-4">
            Whatever the weather, budget or company.
          </p>
          <p className="font-body text-white/75 text-base md:text-lg max-w-2xl mt-4 leading-relaxed">
            Must-sees, local habits, free wins, food, seasons, kids, nights and rain plans — each with a
            link to the guide that covers it properly.
          </p>
        </div>
      </section>

      {/* sections grid */}
      <section className="bg-[#F9F8F6] border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.title} delay={Math.min(i, 3) * 0.05}>
                <div
                  data-testid={`things-card-${i}`}
                  className="bg-white border border-zinc-200 shadow-brut p-6 md:p-7 h-full flex flex-col"
                >
                  <s.icon size={26} className="text-[#DF3226]" />
                  <h2 className="font-display font-900 tracking-tight text-2xl mt-4 leading-tight">{s.title}</h2>
                  <p className="font-body text-sm md:text-base text-black/70 mt-3 leading-relaxed flex-1">{s.body}</p>
                  <div className="flex flex-col gap-2 mt-5">
                    {s.links.map((l, j) => (
                      <Link
                        key={l.to + l.label}
                        to={l.to}
                        data-testid={`things-card-${i}-link-${j}`}
                        className="group inline-flex items-center gap-2 font-body font-700 text-sm text-black/80 hover:text-[#DF3226] transition-colors w-fit"
                      >
                        <ArrowRight size={15} className="text-[#DF3226] shrink-0 group-hover:translate-x-1 transition-transform" />
                        {l.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* planner CTA */}
      <section className="bg-black text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20 flex flex-col md:flex-row md:items-center gap-8">
          <div className="flex-1">
            <h2 className="font-display font-900 tracking-tighter leading-[0.92] text-3xl md:text-5xl">
              Too many options? <span className="font-editorial italic font-600">Let the planner sort them</span>
            </h2>
            <p className="font-body text-white/70 text-base md:text-lg mt-4 max-w-xl leading-relaxed">
              Pick what you fancy — it groups what's close together into realistic days.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/planner"
              data-testid="things-cta-planner"
              className="inline-flex items-center gap-2 font-display font-900 text-base bg-[#DF3226] text-white px-6 py-3.5 rounded-full hover:-translate-y-0.5 hover:shadow-xl transition-all"
            >
              Plan my trip
            </Link>
            <Link
              to="/copenhagen-itinerary"
              data-testid="things-cta-itinerary"
              className="inline-flex items-center gap-2 font-display font-900 text-base bg-white text-black px-6 py-3.5 rounded-full hover:-translate-y-0.5 hover:shadow-xl transition-all"
            >
              The 3-day itinerary
            </Link>
          </div>
        </div>
      </section>

      {/* bookable boat tour — sail the hidden spots */}
      <GygTourBand tourId="1200862" label="PREFER THE WATER? SAIL THE HIDDEN SPOTS" />
      <ExploreLinks
        testid="explore-things"
        links={[
          { label: "Shopping streets beyond Strøget — browse like a local", to: "/copenhagen-shopping-streets" },
          { label: "Streets worth the detour — the photo-worthy streets", to: "/photo-worthy-streets" },
          { label: "The ready-made 3 days — Copenhagen itinerary", to: "/copenhagen-itinerary" },
          { label: "Pick your neighbourhood — the area guides", to: "/areas" },
        ]}
      />

      {/* FAQ — last, before the share bar */}
      <section data-testid="things-faq" className="bg-[#F9F8F6] border-t border-dotted border-zinc-300">
        <div className="max-w-[900px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <Reveal>
            <h2 className="font-display font-900 tracking-tighter text-3xl md:text-4xl mb-8">
              Things-to-do questions, <span className="font-editorial italic font-600">answered</span>
            </h2>
          </Reveal>
          <div className="divide-y divide-zinc-200 border-y border-dotted border-zinc-300">
            {FAQS.map((f, i) => (
              <details key={f.q} data-testid={`things-faq-${i}`} className="group py-5">
                <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                  {f.q}
                  <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                  {f.a}
                </p>
                {(f.links || (f.link ? [f.link] : [])).map((l, j) => (
                  <Link
                    key={l.to + l.label}
                    to={l.to}
                    data-testid={`things-faq-link-${i}-${j}`}
                    className="group flex items-center gap-2 mt-3 font-body font-700 text-base text-black/60 hover:text-[#DF3226] transition-colors w-fit"
                  >
                    <ArrowRight size={16} className="text-[#DF3226] shrink-0 group-hover:translate-x-1 transition-transform" />
                    {l.label}
                  </Link>
                ))}
              </details>
            ))}
          </div>
        </div>
      </section>
      <ShareBar title="Things to Do in Copenhagen" />
    </main>
  );
}
